import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../apiService';

const badge = (bg, color) => ({ background: bg, color, padding: '2px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700 });

export default function StoreTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [edit, setEdit] = useState(null);      // modèle en édition (détail complet)
  const [productQuery, setProductQuery] = useState(''); // recherche dans la liste des produits du modèle
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('restaurant');
  const [creating, setCreating] = useState(false);

  // Extraction intelligente (menu PDF/photos → modèle)
  const [exModal, setExModal] = useState(false);
  const [exFiles, setExFiles] = useState([]);
  const [exName, setExName] = useState('');
  const [exType, setExType] = useState('restaurant');
  const [exRate, setExRate] = useState('');
  const [exPhase, setExPhase] = useState(null); // null = formulaire ; sinon texte de progression
  const [exError, setExError] = useState(null);
  const [exImages, setExImages] = useState(false);     // extraire les photos du document
  const [exGenerate, setExGenerate] = useState(false); // illustrations générées par IA (admin seulement)
  const [genStatus, setGenStatus] = useState({ configured: false, provider: null });
  // Ajout d'images à un modèle existant (depuis l'éditeur)
  const [imgModal, setImgModal] = useState(false);
  const [imgFiles, setImgFiles] = useState([]);
  const [imgExtract, setImgExtract] = useState(true);
  const [imgGenerate, setImgGenerate] = useState(false);
  const [imgOverwrite, setImgOverwrite] = useState(false); // photos Excel : remplacer les images existantes
  const [imgPhase, setImgPhase] = useState(null);
  const [imgError, setImgError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/admin/delivery/store-templates', { userType: 'admin' });
      setTemplates(res?.success ? (res.templates || []) : []);
    } catch (e) { setTemplates([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const togglePublish = async (t) => {
    setBusy(t.template_id + 'pub');
    try {
      await apiService.post(`/admin/delivery/store-templates/${t.template_id}/publish`, { publish: t.visibility !== 'public' }, { userType: 'admin' });
      await load();
    } catch (e) { alert(e?.error || 'Action impossible'); }
    finally { setBusy(null); }
  };

  const del = async (t) => {
    if (!window.confirm(`Supprimer le modèle « ${t.name} » ? Cette action est irréversible.`)) return;
    setBusy(t.template_id + 'del');
    try { await apiService.delete(`/admin/delivery/store-templates/${t.template_id}`, { userType: 'admin' }); await load(); }
    catch (e) { alert(e?.error || 'Suppression impossible'); }
    finally { setBusy(null); }
  };

  const openEdit = async (t) => {
    setProductQuery('');
    setEditLoading(true); setEdit({ loading: true });
    try {
      const res = await apiService.get(`/admin/delivery/store-templates/${t.template_id}`, { userType: 'admin' });
      if (res?.success) setEdit(res.template); else { setEdit(null); alert(res?.error || 'Erreur'); }
    } catch (e) { setEdit(null); alert(e?.error || 'Erreur'); }
    finally { setEditLoading(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await apiService.put(`/admin/delivery/store-templates/${edit.template_id}`, {
        name: edit.name, business_type: edit.business_type, visibility: edit.visibility,
        categories: edit.categories, products: edit.products,
        option_groups: edit.option_groups || [],
      }, { userType: 'admin' });
      setEdit(null); await load();
    } catch (e) { alert(e?.error || 'Enregistrement impossible'); }
    finally { setSaving(false); }
  };

  // Helpers d'édition (immutables sur l'objet edit)
  const setField = (k, v) => setEdit(e => ({ ...e, [k]: v }));
  const updProduct = (i, k, v) => setEdit(e => ({ ...e, products: e.products.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const delProduct = (i) => setEdit(e => ({ ...e, products: e.products.filter((_, idx) => idx !== i) }));
  const updCat = (i, k, v) => setEdit(e => ({ ...e, categories: e.categories.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const delCat = (i) => setEdit(e => ({ ...e, categories: e.categories.filter((_, idx) => idx !== i) }));
  // Image importée à la main : remplace l'image ET ses variantes, et retire la mention IA.
  const updProductImg = (i, image, path, extra = {}) => setEdit(e => ({ ...e, products: e.products.map((p, idx) => idx === i ? { ...p, image, image_gcs_path: path, image_thumb_path: extra.thumb ?? null, image_blurhash: extra.blurhash ?? null, image_is_ai: false } : p) }));
  const updCatImg = (i, image, path) => setEdit(e => ({ ...e, categories: e.categories.map((c, idx) => idx === i ? { ...c, image, image_gcs_path: path } : c) }));
  const addCat = () => setEdit(e => ({ ...e, categories: [...(e.categories || []), { name: 'Nouveau rayon', icon: null, image: null, image_gcs_path: null, subcategories: [] }] }));
  const addProduct = () => setEdit(e => ({ ...e, products: [...(e.products || []), { name: 'Nouveau produit', description: '', price: 0, unit: 'piece', category_name: (e.categories?.[0]?.name || null), subcategory_name: null, images: [], variants: [], extras: [] }] }));

  // ── Extraction intelligente ──
  const openExtract = async () => {
    setExFiles([]); setExName(''); setExType('restaurant'); setExPhase(null); setExError(null);
    setExImages(false); setExGenerate(false);
    setExModal(true);
    loadGenStatus();
    try {
      const res = await apiService.get('/admin/delivery/exchange-rate', { userType: 'admin' });
      if (res?.success) setExRate(String(res.rate));
    } catch (e) { /* champ taux laissé vide → PUT ignoré */ }
  };

  const launchExtract = async () => {
    if (exFiles.length === 0) { alert('Ajoutez au moins un fichier (PDF ou photo du menu).'); return; }
    setExError(null);
    setExPhase('Envoi du document…');
    try {
      // Enregistrer le taux s'il a été modifié (utilisé pour convertir les prix USD).
      const rate = parseInt(exRate, 10);
      if (rate > 0) {
        try { await apiService.put('/admin/delivery/exchange-rate', { rate }, { userType: 'admin' }); } catch (e) {}
      }
      const fd = new FormData();
      exFiles.forEach(f => fd.append('files', f));
      if (exName.trim()) fd.append('name', exName.trim());
      fd.append('business_type', exType.trim() || 'restaurant');
      fd.append('extract_images', exImages ? '1' : '0');
      fd.append('generate_images', exGenerate ? '1' : '0');
      const res = await apiService.upload('/admin/delivery/store-templates/extract', fd, { userType: 'admin' });
      if (!res?.success) throw new Error(res?.error || 'Lancement impossible');

      // Sondage du job jusqu'à la fin (l'analyse prend 30–90 s pour un gros menu).
      const jobId = res.job_id;
      for (;;) {
        await new Promise(r => setTimeout(r, 2500));
        let st;
        try { st = await apiService.get(`/admin/delivery/store-templates/extract/${jobId}`, { userType: 'admin' }); }
        catch (e) { st = null; } // erreur réseau passagère → on réessaie
        if (!st) continue;
        if (st.status === 'processing') { setExPhase(st.phase || 'Analyse en cours…'); continue; }
        if (st.status === 'failed') throw new Error(st.error || 'Extraction échouée');
        // done → relecture obligatoire : on ouvre l'éditeur prérempli
        const s = st.stats || {};
        setExModal(false); setExPhase(null);
        await load();
        alert(`Extraction terminée ✅\n${s.parts > 1 ? `(document volumineux : analysé en ${s.parts} parties fusionnées)\n` : ''}\n${s.categories || 0} rayon(s) · ${s.subcategories || 0} sous-rayon(s)\n${s.products || 0} produit(s) · ${s.option_groups || 0} groupe(s) d'options\nDevise détectée : ${s.currency_detected || '—'}${s.currency_detected === 'USD' || s.currency_detected === 'mixte' ? ` (convertie à ${s.usd_rate} CDF/$)` : ''}\n${s.unpriced ? `⚠ ${s.unpriced} produit(s) sans prix lisible (mis à 0) — à corriger.` : 'Tous les prix ont été lus.'}${imagesSummary(s.images)}\n\nRelisez et corrigez avant de publier.`);
        openEdit({ template_id: st.template_id });
        return;
      }
    } catch (e) {
      setExPhase(null);
      setExError(e?.error || e?.message || 'Extraction échouée');
    }
  };

  // ── Images des produits (V2) ──
  const loadGenStatus = async () => {
    try {
      const st = await apiService.get('/admin/delivery/store-templates/image-gen-status', { userType: 'admin' });
      setGenStatus({ configured: !!st?.configured, provider: st?.provider || null });
    } catch (e) { setGenStatus({ configured: false, provider: null }); }
  };

  const imagesSummary = (im) => {
    if (!im) return '';
    if (im.error) return `\n⚠ Photos : ${im.error}`;
    const parts = [`${im.applied || 0} image(s) ajoutée(s)`];
    if (im.photos_found) parts.push(`${im.photos_matched || 0} photo(s) du document associée(s) sur ${im.photos_found} trouvée(s)`);
    if (im.photos_generated) parts.push(`${im.photos_generated} illustration(s) générée(s) par IA`);
    if (im.generation_errors) parts.push(`${im.generation_errors} génération(s) échouée(s)`);
    if (im.generation_unavailable) parts.push('génération par IA indisponible (clé serveur absente)');
    let out = `\n📷 ${parts.join(' · ')}`;
    const ex = im.excel;
    if (ex) {
      out += `\n📊 Excel : ${ex.photos} photo(s), ${ex.matched} reconnue(s) · ${ex.added} ajoutée(s) · ${ex.replaced} remplacée(s)`
        + (ex.kept_existing ? ` · ${ex.kept_existing} conservée(s) (déjà une image)` : '')
        + (ex.screenshots_skipped ? ` · ${ex.screenshots_skipped} capture(s) d'écran ignorée(s)` : '');
      if (ex.unmatched?.length) out += `\n   Non reconnus : ${ex.unmatched.join(' · ')}`;
    }
    if (im.excel_error) out += `\n⚠ Excel : ${im.excel_error}`;
    return out;
  };

  const openImages = () => {
    setImgFiles([]); setImgExtract(true); setImgGenerate(false); setImgOverwrite(false); setImgPhase(null); setImgError(null);
    setImgModal(true);
    loadGenStatus();
  };

  const launchImages = async () => {
    const extract = imgExtract && imgFiles.length > 0;
    if (!extract && !imgGenerate) { alert('Ajoutez un PDF ou des photos du menu, ou cochez la génération par IA.'); return; }
    setImgError(null);
    setImgPhase('Envoi…');
    try {
      const fd = new FormData();
      imgFiles.forEach(f => fd.append('files', f));
      fd.append('extract_images', extract ? '1' : '0');
      fd.append('generate_images', imgGenerate ? '1' : '0');
      fd.append('overwrite', imgOverwrite ? '1' : '0');
      const res = await apiService.upload(`/admin/delivery/store-templates/${edit.template_id}/images`, fd, { userType: 'admin' });
      if (!res?.success) throw new Error(res?.error || 'Lancement impossible');
      const jobId = res.job_id;
      for (;;) {
        await new Promise(r => setTimeout(r, 2500));
        let st;
        try { st = await apiService.get(`/admin/delivery/store-templates/extract/${jobId}`, { userType: 'admin' }); }
        catch (e) { st = null; }
        if (!st) continue;
        if (st.status === 'processing') { setImgPhase(st.phase || 'Analyse en cours…'); continue; }
        if (st.status === 'failed') throw new Error(st.error || 'Ajout d\'images échoué');
        const im = st.stats?.images || {};
        setImgModal(false); setImgPhase(null);
        await openEdit({ template_id: edit.template_id });
        alert(im.already_complete
          ? 'Tous les produits de ce modèle ont déjà une image : rien à ajouter.'
          : `Images ajoutées ✅${imagesSummary(im)}\n(${st.stats?.missing || 0} produit(s) étaient sans image)\n\nVérifiez le cadrage puis enregistrez.`);
        return;
      }
    } catch (e) {
      setImgPhase(null);
      setImgError(e?.error || e?.message || 'Ajout d\'images échoué');
    }
  };

  const createTemplate = async () => {
    if (!newName.trim() || !newType.trim()) return;
    setCreating(true);
    try {
      const res = await apiService.post('/admin/delivery/store-templates', { name: newName.trim(), business_type: newType.trim() }, { userType: 'admin' });
      if (res?.success) { setCreateModal(false); await load(); openEdit({ template_id: res.template.template_id }); }
      else alert(res?.error || 'Création impossible');
    } catch (e) { alert(e?.error || 'Création impossible'); }
    finally { setCreating(false); }
  };

  // Groupes d'options (suppléments) — rattachés par NOM de rayon/sous-rayon.
  const addGroup = () => setEdit(e => ({ ...e, option_groups: [...(e.option_groups || []), { name: 'Nouveau groupe', max_selections: 0, options: [{ name: '', price_delta: 0 }], attachments: [] }] }));
  const updGroup = (gi, k, v) => setEdit(e => ({ ...e, option_groups: e.option_groups.map((g, idx) => idx === gi ? { ...g, [k]: v } : g) }));
  const delGroup = (gi) => setEdit(e => ({ ...e, option_groups: e.option_groups.filter((_, idx) => idx !== gi) }));
  const updGroupOpt = (gi, oi, k, v) => setEdit(e => ({ ...e, option_groups: e.option_groups.map((g, idx) => idx === gi ? { ...g, options: g.options.map((o, j) => j === oi ? { ...o, [k]: v } : o) } : g) }));
  const addGroupOpt = (gi) => setEdit(e => ({ ...e, option_groups: e.option_groups.map((g, idx) => idx === gi ? { ...g, options: [...(g.options || []), { name: '', price_delta: 0 }] } : g) }));
  const delGroupOpt = (gi, oi) => setEdit(e => ({ ...e, option_groups: e.option_groups.map((g, idx) => idx === gi ? { ...g, options: g.options.filter((_, j) => j !== oi) } : g) }));
  const attKey = (a) => `${a.category_name}::${a.subcategory_name || ''}`;
  const toggleGroupAtt = (gi, att) => setEdit(e => ({
    ...e,
    option_groups: e.option_groups.map((g, idx) => {
      if (idx !== gi) return g;
      const atts = g.attachments || [];
      const has = atts.some(a => attKey(a) === attKey(att));
      return { ...g, attachments: has ? atts.filter(a => attKey(a) !== attKey(att)) : [...atts, att] };
    }),
  }));

  // Sous-rayons
  const addSub = (ci) => setEdit(e => ({ ...e, categories: e.categories.map((c, idx) => idx === ci ? { ...c, subcategories: [...(c.subcategories || []), { name: 'Nouveau sous-rayon', icon: null, image: null, image_gcs_path: null }] } : c) }));
  const updSub = (ci, si, k, v) => setEdit(e => ({ ...e, categories: e.categories.map((c, idx) => idx === ci ? { ...c, subcategories: c.subcategories.map((s, j) => j === si ? { ...s, [k]: v } : s) } : c) }));
  const delSub = (ci, si) => setEdit(e => ({ ...e, categories: e.categories.map((c, idx) => idx === ci ? { ...c, subcategories: c.subcategories.filter((_, j) => j !== si) } : c) }));
  const updSubImg = (ci, si, image, path) => setEdit(e => ({ ...e, categories: e.categories.map((c, idx) => idx === ci ? { ...c, subcategories: c.subcategories.map((s, j) => j === si ? { ...s, image, image_gcs_path: path } : s) } : c) }));

  // Envoi d'une image (produit, rayon, sous-rayon). Le serveur refuse > 5 Mo : l'image est d'abord
  // RÉDUITE dans le navigateur (1 600 px max, JPEG) → quelques centaines de Ko, envoi rapide.
  const [imgUploading, setImgUploading] = useState(false);
  const uploadImageFor = async (file, assign) => {
    if (!file) return;
    setImgUploading(true);
    try {
      let toSend;
      try { toSend = await shrinkImage(file); }
      catch (_) { alert('Format d\'image non lisible. Utilisez une image JPEG, PNG, GIF ou WebP.'); return; }
      const fd = new FormData();
      fd.append('image', toSend);
      const res = await apiService.upload(`/admin/delivery/store-templates/${edit.template_id}/image`, fd, { userType: 'admin' });
      if (res?.success) assign(res.image, res.image_gcs_path, { thumb: res.image_thumb_path || null, blurhash: res.image_blurhash || null });
      else alert(uploadErrorText(res?.error));
    } catch (e) {
      // apiService.upload lève une Error dont le message = réponse du serveur
      alert(uploadErrorText(e?.error || e?.message));
    } finally {
      setImgUploading(false);
    }
  };

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title">📦 Modèles de boutique</h1>
        <div className="page-actions" style={{ display: 'flex', gap: 8 }}>
          <button style={btn('#FF5500')} onClick={openExtract}>🪄 Depuis un menu (PDF/photo)</button>
          <button style={btn('#10B981')} onClick={() => { setNewName(''); setNewType('restaurant'); setCreateModal(true); }}>+ Créer un modèle</button>
          <button className="btn-secondary" onClick={load}>🔄 Actualiser</button>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement…</p></div>
      ) : templates.length === 0 ? (
        <p style={{ color: '#9AA0A8', padding: '2rem', textAlign: 'center' }}>Aucun modèle. Les fournisseurs peuvent en créer depuis leur app (onglet Rayons → « Enregistrer comme modèle »).</p>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Modèle</th><th>Type</th><th>Contenu</th><th>Code</th><th>Visibilité</th><th>Actions</th></tr></thead>
            <tbody>
              {templates.map(t => (
                <tr key={t.template_id}>
                  <td>
                    <div style={{ fontWeight: 600, color: '#1E2229' }}>{t.name}</div>
                    {t.source_business_name && <div style={{ fontSize: '0.8rem', color: '#5C636E' }}>{t.source_business_name}</div>}
                  </td>
                  <td style={{ fontSize: '0.85rem', color: '#414852' }}>{t.business_type}</td>
                  <td style={{ fontSize: '0.85rem', color: '#414852' }}>{t.category_count || 0} rayon(s)<br />{t.product_count || 0} produit(s)</td>
                  <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#C9350F' }}>{t.share_code}</td>
                  <td><span style={t.visibility === 'public' ? badge('#ECFDF5', '#166534') : badge('#F4F5F7', '#414852')}>{t.visibility === 'public' ? 'Public' : 'Privé'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={btn('#C9350F')} disabled={busy} onClick={() => openEdit(t)}>Modifier</button>
                      <button style={btn(t.visibility === 'public' ? '#5C636E' : '#10B981')} disabled={busy} onClick={() => togglePublish(t)}>
                        {t.visibility === 'public' ? 'Dépublier' : 'Publier'}
                      </button>
                      <button style={btn('#dc2626')} disabled={busy} onClick={() => del(t)}>Supprimer</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <div style={overlay} onClick={() => !saving && setEdit(null)}>
          <div style={modalCard} onClick={e => e.stopPropagation()}>
            {edit.loading || editLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#5C636E' }}>Chargement…</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E9EBEF' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <h3 style={{ margin: 0 }}>Modifier le modèle</h3>
                    {imgUploading && <span style={{ fontSize: '0.8rem', color: '#5C636E' }}>⏳ Envoi de l'image…</span>}
                    <button style={btn('#7C3AED')} title="Trouver ou générer des images pour les produits sans image" onClick={openImages}>🖼 Ajouter des images</button>
                  </div>
                  <button onClick={() => setEdit(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#9AA0A8' }}>×</button>
                </div>
                <div style={{ padding: 20, overflowY: 'auto' }}>
                  <label style={lbl}>Nom</label>
                  <input style={inp} value={edit.name || ''} onChange={e => setField('name', e.target.value)} />
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={lbl}>Type de commerce</label>
                      <input style={inp} value={edit.business_type || ''} onChange={e => setField('business_type', e.target.value)} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={lbl}>Visibilité</label>
                      <select style={inp} value={edit.visibility} onChange={e => setField('visibility', e.target.value)}>
                        <option value="private">Privé (code)</option>
                        <option value="public">Public</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...sectWrap }}>
                    <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#1E2229' }}>Rayons ({(edit.categories || []).length})</h4>
                    <button style={btn('#10B981')} onClick={addCat}>+ Ajouter un rayon</button>
                  </div>
                  {(edit.categories || []).map((c, i) => (
                    <div key={i} style={{ borderBottom: '1px solid #F4F5F7', padding: '6px 0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {c.image
                          ? <img src={c.image} alt="" style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover' }} />
                          : <span style={{ fontSize: 20, width: 34, textAlign: 'center' }}>{c.icon || '📦'}</span>}
                        <input style={{ ...inp, margin: 0, flex: 1 }} value={c.name} onChange={e => updCat(i, 'name', e.target.value)} />
                        <label style={{ ...btn('#C9350F'), cursor: 'pointer' }}>
                          Image<input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => uploadImageFor(e.target.files[0], (img, path) => updCatImg(i, img, path))} />
                        </label>
                        <button style={btn('#dc2626')} onClick={() => delCat(i)}>✕</button>
                      </div>
                      {/* Sous-rayons */}
                      <div style={{ paddingLeft: 24, marginTop: 4 }}>
                        {(c.subcategories || []).map((s, si) => (
                          <div key={si} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                            <span style={{ color: '#9AA0A8' }}>↳</span>
                            <input style={{ ...inp, margin: 0, flex: 1 }} value={s.name} onChange={e => updSub(i, si, 'name', e.target.value)} />
                            <label style={{ ...btn('#C9350F'), cursor: 'pointer' }}>
                              Image<input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => uploadImageFor(e.target.files[0], (img, path) => updSubImg(i, si, img, path))} />
                            </label>
                            <button style={btn('#dc2626')} onClick={() => delSub(i, si)}>✕</button>
                          </div>
                        ))}
                        <button style={{ ...btn('#C9350F'), marginTop: 4 }} onClick={() => addSub(i)}>+ Sous-rayon</button>
                      </div>
                    </div>
                  ))}

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...sectWrap }}>
                    <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#1E2229' }}>Produits ({(edit.products || []).length})</h4>
                    <button style={btn('#10B981')} onClick={() => { setProductQuery(''); addProduct(); }}>+ Ajouter un produit</button>
                  </div>
                  {/* Recherche : filtre la liste (nom, rayon, sous-rayon, description), sans accents ni casse.
                      Les modifications portent sur l'index d'origine du produit. */}
                  {(() => {
                    const all = (edit.products || []).map((p, i) => ({ p, i }));
                    const q = normSearch(productQuery);
                    const shown = q
                      ? all.filter(({ p }) => normSearch([p.name, p.category_name, p.subcategory_name, p.description].filter(Boolean).join(' ')).includes(q))
                      : all;
                    return (
                      <>
                        <div style={{ position: 'relative', margin: '4px 0 8px' }}>
                          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9AA0A8', fontSize: '0.9rem', pointerEvents: 'none' }}>🔍</span>
                          <input
                            style={{ ...inp, margin: 0, paddingLeft: 32, paddingRight: 32 }}
                            placeholder="Rechercher un produit (nom, rayon, description)…"
                            value={productQuery}
                            onChange={e => setProductQuery(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Escape') setProductQuery(''); }}
                          />
                          {productQuery && (
                            <button
                              onClick={() => setProductQuery('')}
                              title="Effacer la recherche"
                              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: '1.1rem', color: '#9AA0A8', cursor: 'pointer', padding: '2px 6px' }}
                            >×</button>
                          )}
                        </div>
                        {q && (
                          <div style={{ fontSize: '0.78rem', color: '#5C636E', margin: '0 0 4px' }}>
                            {shown.length === 0
                              ? `Aucun produit ne correspond à « ${productQuery.trim()} ».`
                              : `${shown.length} produit${shown.length > 1 ? 's' : ''} sur ${all.length}`}
                          </div>
                        )}
                        {shown.map(({ p, i }) => (
                    <div key={i} style={{ ...rowItem, flexWrap: 'wrap' }}>
                      {p.image
                        ? <img src={p.image} alt="" style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover' }} />
                        : <span style={{ fontSize: 20, width: 34, textAlign: 'center' }}>🍽️</span>}
                      {p.image_is_ai ? <span title="Illustration générée par IA — ce n'est pas la vraie photo du commerce" style={{ fontSize: '0.62rem', fontWeight: 800, color: '#6D28D9', background: '#EDE9FE', borderRadius: 6, padding: '2px 5px' }}>IA</span> : null}
                      <input style={{ ...inp, margin: 0, flex: 1, minWidth: 120 }} value={p.name} onChange={e => updProduct(i, 'name', e.target.value)} />
                      <input style={{ ...inp, margin: 0, width: 80 }} type="number" value={p.price ?? 0} onChange={e => updProduct(i, 'price', Number(e.target.value))} />
                      <span style={{ fontSize: '0.72rem', color: '#9AA0A8' }}>CDF</span>
                      <select style={{ ...inp, margin: 0, width: 130 }} value={p.category_name || ''} onChange={e => updProduct(i, 'category_name', e.target.value || null)}>
                        <option value="">(Aucun rayon)</option>
                        {(edit.categories || []).map((c, ci) => <option key={ci} value={c.name}>{c.name}</option>)}
                      </select>
                      <label style={{ ...btn('#C9350F'), cursor: 'pointer' }}>
                        Image<input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => uploadImageFor(e.target.files[0], (img, path, extra) => updProductImg(i, img, path, extra))} />
                      </label>
                      <button style={btn('#dc2626')} onClick={() => delProduct(i)}>✕</button>
                    </div>
                        ))}
                      </>
                    );
                  })()}

                  {/* Options & suppléments — groupes attachés aux rayons/sous-rayons (par nom) */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...sectWrap }}>
                    <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#1E2229' }}>Options & suppléments ({(edit.option_groups || []).length})</h4>
                    <button style={btn('#10B981')} onClick={addGroup}>+ Ajouter un groupe</button>
                  </div>
                  <p style={{ fontSize: '0.78rem', color: '#9AA0A8', margin: '0 0 6px' }}>
                    Ex. « Supplément fromage +500 FC », « Make it a meal +3 000 FC ». Un groupe s'attache à des rayons/sous-rayons : tous leurs produits le proposent (cases à cocher côté client).
                  </p>
                  {(edit.option_groups || []).map((g, gi) => (
                    <div key={gi} style={{ border: '1px solid #E9EBEF', borderRadius: 10, padding: 10, marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input style={{ ...inp, margin: 0, flex: 1 }} value={g.name} placeholder="Nom du groupe (ex. Suppléments)" onChange={e => updGroup(gi, 'name', e.target.value)} />
                        <span style={{ fontSize: '0.72rem', color: '#9AA0A8', whiteSpace: 'nowrap' }}>Max choix</span>
                        <input style={{ ...inp, margin: 0, width: 64 }} type="number" min="0" title="0 = illimité" value={g.max_selections ?? 0} onChange={e => updGroup(gi, 'max_selections', Math.max(0, Number(e.target.value) || 0))} />
                        <button style={btn('#dc2626')} onClick={() => delGroup(gi)}>✕</button>
                      </div>
                      <div style={{ paddingLeft: 16, marginTop: 6 }}>
                        {(g.options || []).map((o, oi) => (
                          <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                            <span style={{ color: '#9AA0A8' }}>☑</span>
                            <input style={{ ...inp, margin: 0, flex: 1 }} value={o.name} placeholder="Ex. Supplément fromage" onChange={e => updGroupOpt(gi, oi, 'name', e.target.value)} />
                            <input style={{ ...inp, margin: 0, width: 90 }} type="number" min="0" value={o.price_delta ?? 0} onChange={e => updGroupOpt(gi, oi, 'price_delta', Math.max(0, Number(e.target.value) || 0))} />
                            <span style={{ fontSize: '0.72rem', color: '#9AA0A8' }}>+CDF</span>
                            <button style={btn('#dc2626')} onClick={() => delGroupOpt(gi, oi)} disabled={(g.options || []).length <= 1}>✕</button>
                          </div>
                        ))}
                        <button style={{ ...btn('#C9350F'), marginTop: 4 }} onClick={() => addGroupOpt(gi)}>+ Option</button>
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: '0.75rem', color: '#5C636E', fontWeight: 600, marginBottom: 3 }}>Rayons / sous-rayons concernés</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
                          {(edit.categories || []).map((c, ci) => {
                            const entries = [{ category_name: c.name, subcategory_name: null, label: c.name }]
                              .concat((c.subcategories || []).map(s => ({ category_name: c.name, subcategory_name: s.name, label: `${c.name} › ${s.name}` })));
                            return entries.map((a, ai) => {
                              const checked = (g.attachments || []).some(x => attKey(x) === attKey(a));
                              return (
                                <label key={`${ci}-${ai}`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', color: '#414852', cursor: 'pointer' }}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleGroupAtt(gi, { category_name: a.category_name, subcategory_name: a.subcategory_name })} />
                                  {a.label}
                                </label>
                              );
                            });
                          })}
                        </div>
                        {(g.attachments || []).length === 0 && (
                          <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: 3 }}>⚠ Sans rattachement, ce groupe sera ignoré à l'enregistrement.</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '14px 20px', borderTop: '1px solid #E9EBEF', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button style={{ ...btn('#E9EBEF'), color: '#414852' }} disabled={saving} onClick={() => setEdit(null)}>Annuler</button>
                  <button style={btn('#10B981')} disabled={saving} onClick={saveEdit}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {exModal && (
        <div style={overlay} onClick={() => !exPhase && setExModal(false)}>
          <div style={{ ...modalCard, width: 'min(520px, 96vw)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E9EBEF' }}>
              <h3 style={{ margin: 0 }}>🪄 Créer un modèle depuis un menu</h3>
            </div>
            {exPhase ? (
              <div style={{ padding: '36px 24px', textAlign: 'center' }}>
                <div className="spinner" style={{ margin: '0 auto 14px' }}></div>
                <div style={{ fontWeight: 600, color: '#1E2229' }}>{exPhase}</div>
                <div style={{ fontSize: '0.8rem', color: '#9AA0A8', marginTop: 8 }}>
                  L'analyse d'un gros menu peut prendre une à deux minutes. Ne fermez pas cette fenêtre.
                </div>
              </div>
            ) : (
              <>
                <div style={{ padding: 20 }}>
                  <p style={{ fontSize: '0.82rem', color: '#5C636E', marginTop: 0 }}>
                    Envoyez le menu d'un restaurant ou la liste de produits d'un commerce (PDF, ou photos — une par page).
                    L'IA détecte rayons, sous-rayons, produits, prix et options, puis <b>l'éditeur s'ouvre pour relecture</b> avant publication.
                  </p>
                  <label style={lbl}>Fichier(s) — PDF, JPEG, PNG, WebP (6 max, 25 Mo chacun)</label>
                  <input
                    type="file" multiple accept=".pdf,image/jpeg,image/png,image/webp" style={{ ...inp, padding: 8 }}
                    onChange={e => setExFiles(Array.from(e.target.files || []))}
                  />
                  {exFiles.length > 0 && (
                    <div style={{ fontSize: '0.78rem', color: '#414852', marginTop: 4 }}>
                      {exFiles.map((f, i) => <div key={i}>📄 {f.name} ({(f.size / 1024 / 1024).toFixed(1)} Mo)</div>)}
                    </div>
                  )}
                  <label style={lbl}>Nom du modèle (optionnel)</label>
                  <input style={inp} value={exName} onChange={e => setExName(e.target.value)} placeholder="Ex. Menu Chez Mado" />
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={lbl}>Type de commerce</label>
                      <input style={inp} value={exType} onChange={e => setExType(e.target.value)} placeholder="restaurant, supermarket…" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={lbl}>Taux USD→CDF (si prix en $)</label>
                      <input style={inp} type="number" value={exRate} onChange={e => setExRate(e.target.value)} placeholder="Ex. 2800" />
                    </div>
                  </div>
                  <label style={lbl}>Photos des produits</label>
                  <label style={chk}>
                    <input type="checkbox" checked={exImages} onChange={e => setExImages(e.target.checked)} />
                    <span>Extraire les photos des plats du document <span style={muted}>— exactes depuis un PDF fabriqué ; approximatives depuis une photo de menu imprimé (cadrage à relire)</span></span>
                  </label>
                  <label style={{ ...chk, opacity: genStatus.configured ? 1 : 0.55 }}>
                    <input type="checkbox" checked={exGenerate} disabled={!genStatus.configured} onChange={e => setExGenerate(e.target.checked)} />
                    <span>Générer une illustration par IA pour les produits restés sans photo <span style={muted}>— {genStatus.configured ? `${genStatus.provider} · ce n'est pas la vraie photo du commerce (badge « IA »)` : 'indisponible : ajoutez GEMINI_API_KEY ou OPENAI_API_KEY sur Railway'}</span></span>
                  </label>
                  {exError && <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef2f2', color: '#B91C1C', borderRadius: 8, fontSize: '0.82rem' }}>⚠ {exError}</div>}
                </div>
                <div style={{ padding: '14px 20px', borderTop: '1px solid #E9EBEF', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button style={{ ...btn('#E9EBEF'), color: '#414852' }} onClick={() => setExModal(false)}>Annuler</button>
                  <button style={btn('#FF5500')} disabled={exFiles.length === 0} onClick={launchExtract}>Lancer l'analyse</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {imgModal && edit && (
        <div style={overlay} onClick={() => !imgPhase && setImgModal(false)}>
          <div style={{ ...modalCard, zIndex: 1001 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E9EBEF' }}>
              <h3 style={{ margin: 0 }}>🖼 Ajouter des images — {edit.name}</h3>
            </div>
            {imgPhase ? (
              <div style={{ padding: '36px 24px', textAlign: 'center' }}>
                <div className="spinner" style={{ margin: '0 auto 14px' }}></div>
                <div style={{ fontWeight: 600, color: '#1E2229' }}>{imgPhase}</div>
                <div style={{ fontSize: '0.8rem', color: '#9AA0A8', marginTop: 8 }}>Ne fermez pas cette fenêtre.</div>
              </div>
            ) : (
              <>
                <div style={{ padding: 20 }}>
                  <p style={{ fontSize: '0.82rem', color: '#5C636E', marginTop: 0 }}>
                    Seuls les produits <b>sans image</b> sont concernés ({(edit.products || []).filter(p => !p.image && !p.image_gcs_path).length} sur {(edit.products || []).length}), sauf si vous cochez le remplacement pour un fichier Excel.
                  </p>
                  <label style={lbl}>Menu — PDF, JPEG, PNG, WebP, ou Excel .xlsx (photo en colonne A, nom du produit en colonne B) — optionnel si génération par IA</label>
                  <input type="file" multiple accept=".pdf,.xlsx,image/jpeg,image/png,image/webp" style={{ ...inp, padding: 8 }} onChange={e => setImgFiles(Array.from(e.target.files || []))} />
                  {imgFiles.length > 0 && (
                    <div style={{ fontSize: '0.78rem', color: '#414852', marginTop: 4 }}>
                      {imgFiles.map((f, i) => <div key={i}>📄 {f.name} ({(f.size / 1024 / 1024).toFixed(1)} Mo)</div>)}
                    </div>
                  )}
                  <label style={lbl}>Options</label>
                  <label style={{ ...chk, opacity: imgFiles.length ? 1 : 0.55 }}>
                    <input type="checkbox" checked={imgExtract} disabled={!imgFiles.length} onChange={e => setImgExtract(e.target.checked)} />
                    <span>Extraire les photos des plats du document <span style={muted}>— exactes depuis un PDF fabriqué ou un Excel ; approximatives depuis une photo de menu imprimé</span></span>
                  </label>
                  {imgFiles.some(f => /\.xlsx$/i.test(f.name)) && (
                    <label style={{ ...chk, opacity: imgExtract ? 1 : 0.55 }}>
                      <input type="checkbox" checked={imgOverwrite} disabled={!imgExtract} onChange={e => setImgOverwrite(e.target.checked)} />
                      <span>Remplacer les images existantes par les photos du fichier Excel <span style={muted}>— une capture d'écran ne remplace jamais une image existante</span></span>
                    </label>
                  )}
                  <label style={{ ...chk, opacity: genStatus.configured ? 1 : 0.55 }}>
                    <input type="checkbox" checked={imgGenerate} disabled={!genStatus.configured} onChange={e => setImgGenerate(e.target.checked)} />
                    <span>Générer une illustration par IA pour les produits restés sans photo <span style={muted}>— {genStatus.configured ? `${genStatus.provider} · badge « IA », ce n'est pas la vraie photo du commerce` : 'indisponible : ajoutez GEMINI_API_KEY ou OPENAI_API_KEY sur Railway'}</span></span>
                  </label>
                  {imgError && <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef2f2', color: '#B91C1C', borderRadius: 8, fontSize: '0.82rem' }}>⚠ {imgError}</div>}
                </div>
                <div style={{ padding: '14px 20px', borderTop: '1px solid #E9EBEF', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button style={{ ...btn('#E9EBEF'), color: '#414852' }} onClick={() => setImgModal(false)}>Annuler</button>
                  <button style={btn('#7C3AED')} disabled={!(imgExtract && imgFiles.length) && !imgGenerate} onClick={launchImages}>Lancer</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {createModal && (
        <div style={overlay} onClick={() => !creating && setCreateModal(false)}>
          <div style={{ ...modalCard, width: 'min(440px, 96vw)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E9EBEF' }}><h3 style={{ margin: 0 }}>Créer un modèle vierge</h3></div>
            <div style={{ padding: 20 }}>
              <label style={lbl}>Nom du modèle</label>
              <input style={inp} value={newName} onChange={e => setNewName(e.target.value)} placeholder="Ex. Menu standard restaurant" />
              <label style={lbl}>Type de commerce</label>
              <input style={inp} value={newType} onChange={e => setNewType(e.target.value)} placeholder="restaurant, cafe, supermarket…" />
              <p style={{ fontSize: '0.8rem', color: '#9AA0A8', marginTop: 8 }}>Vous ajouterez ensuite les rayons et produits dans l'éditeur.</p>
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid #E9EBEF', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button style={{ ...btn('#E9EBEF'), color: '#414852' }} disabled={creating} onClick={() => setCreateModal(false)}>Annuler</button>
              <button style={btn('#10B981')} disabled={creating || !newName.trim() || !newType.trim()} onClick={createTemplate}>{creating ? 'Création…' : 'Créer et éditer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Réduit une image avant l'envoi : côté le plus long ≤ 1 600 px, JPEG qualité 0,85, fond blanc
// (transparence PNG). GIF et petites images déjà acceptées : envoyées telles quelles.
// Une photo HEIC est convertie si le navigateur sait la lire (Safari) ; sinon → erreur lisible.
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_SIDE = 1600;
async function shrinkImage(file) {
  if (file.type === 'image/gif') return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) throw new Error('image illisible');
    const small = Math.max(w, h) <= MAX_SIDE && file.size <= 1.5 * 1024 * 1024 && ACCEPTED_TYPES.includes(file.type);
    if (small) return file;
    const k = Math.min(1, MAX_SIDE / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * k);
    canvas.height = Math.round(h * k);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) throw new Error('conversion impossible');
    const base = (file.name || 'image').replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Message d'erreur d'envoi lisible (les réponses du serveur sont en anglais)
function uploadErrorText(msg) {
  const m = String(msg || '');
  if (/too large|5MB/i.test(m)) return 'Image trop lourde (5 Mo maximum).';
  if (/Only image files/i.test(m)) return 'Format non accepté (JPEG, PNG, GIF ou WebP).';
  return m ? `Envoi impossible : ${m}` : 'Envoi impossible.';
}

// Recherche sans accents ni casse (« creme » trouve « Crème brûlée »)
const normSearch = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

const btn = (color) => ({ background: color, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' });
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalCard = { background: '#fff', borderRadius: 14, width: 'min(680px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column' };
const lbl = { display: 'block', fontSize: '0.75rem', color: '#5C636E', fontWeight: 600, margin: '10px 0 3px' };
const inp = { width: '100%', padding: '8px 10px', border: '1px solid #DCE0E6', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const chk = { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.84rem', color: '#1E2229', margin: '6px 0', cursor: 'pointer' };
const muted = { color: '#9AA0A8', fontWeight: 400 };
const sect = { margin: '18px 0 6px', fontSize: '0.95rem', color: '#1E2229' };
const sectWrap = { margin: '18px 0 6px' };
const rowItem = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #F4F5F7' };
